/**
 * Plant agents in words (routes/platform/plant-agents.tsx): an identity's state, how long ago its PC was heard from,
 * the table's order, and (agent 0.3.0) its last update, its mode and the settings managed for it from the cloud; the
 * agent releases' sizes and states. Pure.
 */
import type {
  AgentRelease,
  IngestAgent,
  IngestAgentConsent,
  IngestAgentConsentEvent,
  IngestExploreConsent,
  IngestManagedSettings,
  IngestPath,
  IngestSendMode,
  IngestUpdatePolicy,
} from "@/lib/api/ingest-agents";

/** A moment as the browser reads it, e.g. "Oct 8, 03:30 PM". */
export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** How long ago a moment was, at now: "just now", "12 min ago", "5 h ago", "41 days ago". */
export function agoWords(iso: string, now: number): string {
  const minutes = Math.floor((now - Date.parse(iso)) / 60_000);
  if (!Number.isFinite(minutes)) return "";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

export type Tone = "success" | "warning" | "secondary";

/** An identity's state in words: its badge, and a line under it. */
export function agentState(a: IngestAgent): { text: string; tone: Tone; detail: string } {
  if (a.status === "revoked") return { text: "Revoked", tone: "secondary", detail: a.revoked_at ? `on ${fmtWhen(a.revoked_at)}` : "" };
  if (a.status === "active") {
    return { text: "Connected", tone: "success", detail: a.activated_at ? `since ${fmtWhen(a.activated_at)}` : "" };
  }
  if (a.setup_expired) return { text: "Setup key expired", tone: "secondary", detail: "re-issue to set up a PC" };
  return { text: "Waiting for setup", tone: "warning", detail: `the file works until ${fmtWhen(a.setup_expires_at)}` };
}

export type PresenceTone = "online" | "paused" | "offline";

/**
 * Agent 0.3.2: whether the PC's agent reaches Sahas Forge now: online (and how it connects), paused by someone at the
 * PC, or offline since when. Null when unknown (never heard from, not active, or an API before presence).
 */
export function presenceWords(a: IngestAgent): { tone: PresenceTone; text: string; title: string } | null {
  const p = a.presence;
  if (!p || p.state === "unknown") return null;
  const relayed = !!p.via && p.via !== "direct";
  // The gateway by name when the API knows it ("via Gateway 7E1A90A1"); else just "via gateway".
  const via = relayed ? (p.via_gateway_name ? `via ${p.via_gateway_name}` : "via gateway") : "direct";
  const lag = p.live ? "" : " (an agent before 0.3.0: known within a pass)";
  if (p.state === "offline") {
    return { tone: "offline", text: "Offline", title: `${p.since ? `Offline since ${fmtWhen(p.since)}` : "Offline"}${lag}` };
  }
  const since = p.since ? `, since ${fmtWhen(p.since)}` : "";
  if (p.agent_state === "paused") return { tone: "paused", text: "Paused at the PC", title: `Online, but paused by someone at the PC${since}` };
  return { tone: "online", text: `Online · ${via}`, title: `Online ${relayed ? (p.via_gateway_name ? `through gateway ${p.via_gateway_name}` : `through ${p.via}`) : "directly"}${since}${lag}` };
}

/** How far back "last relayed through …" is still shown: a PC that went direct a week ago is not "relayed" now. */
export const RELAYED_RECENT_MS = 7 * 24 * 3_600_000;

/**
 * The second line of "Last heard from": the gateway that last relayed this PC, and how long ago ("Last relayed through
 * Gateway 7E1A90A1, 5 min ago"), while that was within a week. Null for a PC never relayed, or not for a week, and for a
 * revoked identity. The gateway's name is the API's; without it (the gateway was deleted, or an older API) it says "a
 * gateway".
 */
export function relayedWords(a: IngestAgent, now: number): { text: string; title: string } | null {
  if (a.status === "revoked" || !a.last_relayed_at) return null;
  const at = Date.parse(a.last_relayed_at);
  if (!Number.isFinite(at) || now - at > RELAYED_RECENT_MS) return null;
  const who = a.last_relayed_by_name ? a.last_relayed_by_name : "a gateway";
  return { text: `Last relayed through ${who}, ${agoWords(a.last_relayed_at, now)}`, title: fmtWhen(a.last_relayed_at) };
}

/** Connected PCs first, then the ones waiting for setup, then the revoked; newest first within each. */
export function sortAgents(agents: IngestAgent[]): IngestAgent[] {
  const rank = (a: IngestAgent) => (a.status === "active" ? 0 : a.status === "issued" ? 1 : 2);
  return [...agents].sort((x, y) => rank(x) - rank(y) || y.created_at.localeCompare(x.created_at));
}

// ── Agent 0.3.0: updates, mode, managed settings ─────────────────────────────

export const MODE_WORDS: Record<IngestSendMode, string> = { probe: "Probe", operational: "Operational" };

export const MODE_HINTS: Record<IngestSendMode, string> = {
  probe: "A newly set-up PC: the newest files of each kind go first, then everything else, for building readers.",
  operational: "Only files whose kind has a verified reader.",
};

export const POLICY_WORDS: Record<IngestUpdatePolicy, string> = {
  auto: "Automatic",
  notify: "Notify only",
  off: "Off",
};

export const PATH_WORDS: Record<IngestPath, string> = {
  auto: "Automatic",
  "direct-only": "Direct only",
  "relay-only": "Through a gateway only",
};

// ── Agent 0.4.0: the explore consent ─────────────────────────────────────────

/** The opt-ins of an explore consent, as boxes: what the plant can agree to besides the folders. */
export type ConsentFlag = "processes" | "arp" | "user" | "whole_drives" | "opcua_test" | "sql_test" | "net_test";

export const CONSENT_FLAGS: readonly ConsentFlag[] = [
  "processes",
  "arp",
  "user",
  "whole_drives",
  "opcua_test",
  "sql_test",
  "net_test",
];

/** Each opt-in in words: its box, a line under it, and a few words for a summary. */
export const CONSENT_FLAG_WORDS: Record<ConsentFlag, { label: string; hint: string; short: string }> = {
  processes: {
    label: "Running processes",
    hint: "Names, CPU and memory of the programs running. Never command lines or users.",
    short: "running processes",
  },
  arp: {
    label: "ARP cache",
    hint: "The devices this PC talked to lately on its network: their IP and MAC addresses.",
    short: "ARP cache",
  },
  user: {
    label: "Logged-on user's name",
    hint: "Without it, the documents hold no user name.",
    short: "logged-on user's name",
  },
  whole_drives: {
    label: "Whole drives",
    hint: "Deep scans of whole drives, and so of any folder.",
    short: "whole drives",
  },
  opcua_test: {
    label: "OPC UA server on the PC",
    hint: "Reads one OPC UA server on this PC: in an explore run, and in OPC UA browse.",
    short: "OPC UA server on the PC",
  },
  sql_test: {
    label: "SQL Server on the PC",
    hint: "Logs in to the PC's own SQL Server with the Windows login and reads its catalogue.",
    short: "SQL Server on the PC",
  },
  net_test: {
    label: "Link test",
    hint: "Tests the link to Sahas Forge, and to a gateway.",
    short: "link test",
  },
};

/**
 * Who asked for the explore consent and when, as the cloud wrote it ("asked by a@b.c on Oct 14, 03:30 PM"), or null
 * when it wrote neither. An admin asks; someone at the PC confirms (consentStatusWords).
 */
export function consentStamp(c: Pick<IngestExploreConsent, "given_by" | "given_at"> | null | undefined): string | null {
  if (!c || (!c.given_by && !c.given_at)) return null;
  return `asked${c.given_by ? ` by ${c.given_by}` : ""}${c.given_at ? ` on ${fmtWhen(c.given_at)}` : ""}`;
}

/** Said wherever the explore consent is asked or shown (Arjun's decision on #1419, 2026-10-14). */
export const CONSENT_STAFF_NOTICE = "Sahas staff and your organisation's admins can read the documents this check sends.";

/** One of the API's names for a consent's item ("processes", "deep root D:\X") in words. */
export function consentNameWords(name: string): string {
  if (name.startsWith("deep root ")) return `deep scans of ${name.slice("deep root ".length)}`;
  return CONSENT_FLAG_WORDS[name as ConsentFlag]?.short ?? name;
}

/** A consent's items in words, or "nothing beyond the basic inventory". */
export function consentItemsWords(c: IngestExploreConsent | null | undefined): string {
  if (!c) return "nothing beyond the basic inventory";
  const parts: string[] = CONSENT_FLAGS.filter((k) => c[k]).map((k) => CONSENT_FLAG_WORDS[k].short);
  for (const r of c.deep_roots ?? []) parts.push(`deep scans of ${r}`);
  return parts.length ? parts.join(", ") : "nothing beyond the basic inventory";
}

export type ConsentTone = "success" | "warning" | "secondary";

/**
 * Where the explore consent stands at the PC, in words: confirmed there (by whom, when), waiting for the PC to confirm
 * (all of it, or some), or declined. Null when nothing is asked.
 */
export function consentStatusWords(c: IngestAgentConsent | null | undefined): { tone: ConsentTone; text: string } | null {
  if (!c || !c.asked) return null;
  const by = (who: string | null, at: string | null) => `${who ? ` by ${who}` : ""}${at ? ` on ${fmtWhen(at)}` : ""}`;
  const waiting = c.waiting.map(consentNameWords).join(", ");
  if (c.confirmed && c.waiting.length === 0) {
    return { tone: "success", text: `Confirmed at the PC${by(c.confirmed_by, c.confirmed_at)}.` };
  }
  if (c.confirmed) {
    return { tone: "warning", text: `Confirmed at the PC${by(c.confirmed_by, c.confirmed_at)}, in part. Waiting for the PC to confirm: ${waiting}.` };
  }
  const last = c.last_answer;
  if (last && (last.event === "declined" || last.event === "withdrawn")) {
    const what = last.event === "declined" ? "Declined" : "Withdrawn";
    return { tone: "secondary", text: `${what} at the PC${by(last.by.replace(/^at the PC: /, ""), last.at)}. Nothing of it applies until someone there confirms it.` };
  }
  return { tone: "warning", text: `Waiting for the PC to confirm: ${waiting}. Nothing of it applies until someone at the PC confirms it in the agent's window.` };
}

const EVENT_WORDS: Record<IngestAgentConsentEvent["event"], string> = {
  asked: "asked for",
  narrowed: "narrowed to",
  confirmed: "confirmed",
  declined: "declined",
  withdrawn: "withdrew",
};

/** One change of the consent in a line: "Oct 14, 03:30 PM · arun@x asked for running processes". */
export function consentEventWords(e: IngestAgentConsentEvent): string {
  const who = e.source === "pc" ? `${e.by.replace(/^at the PC: /, "")} at the PC` : e.by;
  return `${fmtWhen(e.at)} · ${who} ${EVENT_WORDS[e.event] ?? e.event} ${consentItemsWords(e.consent)}`;
}

/** An explore consent in a line: what is agreed, then who gave it. */
export function consentWords(c: IngestExploreConsent): string {
  const parts: string[] = CONSENT_FLAGS.filter((k) => c[k]).map((k) => CONSENT_FLAG_WORDS[k].short);
  if (c.deep_roots?.length) parts.push(`deep scans of ${c.deep_roots.join("; ")}`);
  const agreed = parts.length ? parts.join(", ") : "nothing beyond the basic inventory";
  const stamp = consentStamp(c);
  return stamp ? `${agreed} (${stamp})` : agreed;
}

/** Whether any setting of the PC is managed from the cloud. */
export function isManaged(a: Pick<IngestAgent, "managed_settings">): boolean {
  return Object.keys(a.managed_settings ?? {}).length > 0;
}

/** The mode the PC sends in: "Probe" or "Operational" when managed, else "PC's own". */
export function modeWords(a: Pick<IngestAgent, "managed_settings">): string {
  const mode = a.managed_settings?.mode;
  return mode ? MODE_WORDS[mode] : "PC's own";
}

export type UpdateTone = "success" | "warning" | "danger";

/**
 * The agent's last update in words, or null when it has reported none: "Updated to 0.3.1", "Rolled back from 0.3.1"
 * (the new version failed its checks, so the PC went back) or "Update to 0.3.1 failed", with when and why.
 */
export function updateWords(
  a: Pick<IngestAgent, "update_state" | "update_version" | "update_error" | "update_at">,
  now: number,
): { text: string; tone: UpdateTone; ago: string; at: string | null; error: string | null } | null {
  if (!a.update_state || !a.update_version) return null;
  const v = a.update_version;
  const [text, tone]: [string, UpdateTone] =
    a.update_state === "updated"
      ? [`Updated to ${v}`, "success"]
      : a.update_state === "rolled_back"
        ? [`Rolled back from ${v}`, "warning"]
        : [`Update to ${v} failed`, "danger"];
  return {
    text,
    tone,
    ago: a.update_at ? agoWords(a.update_at, now) : "",
    at: a.update_at,
    error: a.update_error?.trim() || null,
  };
}

/** Whether the PC's settings are managed from the cloud, how, by whom and how long ago. */
export function managedWords(
  a: Pick<IngestAgent, "managed_settings" | "managed_by" | "managed_at">,
  now: number,
): { managed: boolean; text: string; control: string; by: string } {
  if (!isManaged(a)) return { managed: false, text: "PC's own", control: "", by: "" };
  const control = a.managed_settings.control === "local" ? "the PC can change them" : "locked on the PC";
  const when = a.managed_at ? agoWords(a.managed_at, now) : "";
  const by = [a.managed_by ? `by ${a.managed_by}` : "", when].filter(Boolean).join(", ");
  return { managed: true, text: "Managed", control, by };
}

/** The settings managed for a PC, one [label, value] each, in the order of the settings dialog. */
export function managedSettingLines(s: IngestManagedSettings): [string, string][] {
  const lines: [string, string][] = [];
  if (s.mode) lines.push(["Mode", MODE_WORDS[s.mode]]);
  if (s.samples_per_kind !== undefined) lines.push(["Samples per kind", String(s.samples_per_kind)]);
  if (s.drop !== undefined) lines.push(["Drag and drop", s.drop ? "On" : "Off"]);
  if (s.bandwidth) {
    const parts = [
      s.bandwidth.catch_up_pct !== undefined ? `${s.bandwidth.catch_up_pct}% while catching up` : "",
      s.bandwidth.after_pct !== undefined ? `${s.bandwidth.after_pct}% afterwards` : "",
    ].filter(Boolean);
    if (parts.length) lines.push(["Bandwidth", `${parts.join(", ")} (of the measured speed)`]);
  }
  if (s.updates) {
    const parts = [
      s.updates.policy ? POLICY_WORDS[s.updates.policy] : "",
      s.updates.pin ? `pinned to ${s.updates.pin}` : "",
      s.updates.urgent ? "forced (urgent)" : "",
    ].filter(Boolean);
    if (parts.length) lines.push(["Updates", parts.join(", ")]);
  }
  if (s.path) lines.push(["Route", PATH_WORDS[s.path]]);
  if (s.interval_minutes !== undefined) lines.push(["Interval", `${s.interval_minutes} min`]);
  if (s.max_per_pass !== undefined) lines.push(["Files per pass", String(s.max_per_pass)]);
  if (s.folders) {
    const words = s.folders.map((f) => `${f.path}${f.recursive ? " (and subfolders)" : ""}${f.existing === "new" ? ", new files only" : ""}`);
    lines.push(["Watched folders", words.length ? words.join("; ") : "None"]);
  }
  if (s.patterns) lines.push(["File patterns", s.patterns.join(", ")]);
  if (s.remote_browse !== undefined) lines.push(["Remote browse", s.remote_browse ? "On" : "Off"]);
  if (s.pendrive_save !== undefined) lines.push(["Save to a pendrive", s.pendrive_save ? "On" : "Off"]);
  if (s.explore_consent) lines.push(["Explore consent", consentWords(s.explore_consent)]);
  return lines;
}

// ── Agent releases (platform admins) ─────────────────────────────────────────

/** A size in bytes, e.g. "840 KB" or "12.4 MB". */
export function fmtSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Releases still offered first (newest first), then the withdrawn. */
export function sortReleases(releases: AgentRelease[]): AgentRelease[] {
  const rank = (r: AgentRelease) => (r.withdrawn_at ? 1 : 0);
  return [...releases].sort((x, y) => rank(x) - rank(y) || y.created_at.localeCompare(x.created_at));
}
